import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, AlertCircle, X, KeyRound } from 'lucide-react'
import { Button, Input } from '@/components/atoms'
import { Checkbox } from '@/components/ui/checkbox'
import { GlowEffect } from '@/components/ui/GlowEffect'
import { typography, formStyles } from '@/constants/styles'
import SignInBackground from '@/assets/SignIn.png'
import type { LoginScreenBaseProps } from './LoginScreenBase'

export const LoginScreenDesktop = ({
  formData,
  loginMode,
  otpSent,
  showPassword,
  rememberMe,
  loading,
  sendingOtp,
  error,
  onFormDataChange,
  onLoginModeChange,
  onSendOtp,
  onShowPasswordChange,
  onRememberMeChange,
  onClearField,
  onSubmit,
}: LoginScreenBaseProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-[#f2f1f6] dark:bg-[linear-gradient(178deg,rgba(27,27,25,1)_1%,rgba(37,35,14,1)_52%,rgba(27,27,25,1)_97%)] relative flex items-center justify-center p-4 sm:p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-[520px] z-10"
      >
        <div className="text-center mb-8">
          <h1 className={typography.h1}>
            Welcome Back
          </h1>
          <p className={typography.body}>
            Sign in to your SecondLane account
          </p>
        </div>

        <div className={`${formStyles.card} p-6 sm:p-8`}>
          {/* Light theme: background image */}
          <div 
            className="absolute inset-0 dark:hidden"
            style={{ 
              backgroundImage: `url(${SignInBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
          {/* Dark theme: green glow effect */}
          <GlowEffect className="hidden dark:block" />
          <div className="relative z-10">
            <form onSubmit={onSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 flex items-center gap-2 rounded">
                  <AlertCircle size={18} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div>
                <label className={typography.labelBlock}>
                  Email
                </label>
                <Input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  leftElement={<Mail size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                  rightElement={formData.email ? (
                    <button
                      type="button"
                      onClick={() => onClearField('email')}
                      className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors"
                    >
                      <X size={18} />
                    </button>
                  ) : undefined}
                  className={formStyles.input}
                  required
                  disabled={otpSent && loginMode === 'otp'}
                />
              </div>

              {loginMode === 'otp' ? (
                <>
                  {!otpSent ? (
                    <>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={onSendOtp}
                        className="w-full h-12 bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(255,255,255,0.72)] hover:bg-[rgba(45,41,35,0.85)] dark:hover:bg-[rgba(255,255,255,0.85)] text-white dark:text-[#2d2923] hover:text-white dark:hover:text-[#2d2923] font-plus-jakarta font-medium text-[14px] leading-[20px] rounded-lg transition-all duration-200"
                        isLoading={sendingOtp}
                        disabled={!formData.email || sendingOtp}
                      >
                        SEND OTP CODE
                      </Button>
                      <button
                        type="button"
                        onClick={() => onLoginModeChange('password')}
                        className="w-full text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors font-plus-jakarta font-medium text-[14px] underline"
                      >
                        Sign in with password
                      </button>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className={typography.labelBlock}>
                          OTP Code
                        </label>
                        <Input
                          type="text"
                          name="otp"
                          value={formData.otp}
                          onChange={handleChange}
                          placeholder="Enter OTP code"
                          leftElement={<KeyRound size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                          rightElement={formData.otp ? (
                            <button
                              type="button"
                              onClick={() => onClearField('otp')}
                              className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors"
                            >
                              <X size={18} />
                            </button>
                          ) : undefined}
                          className={formStyles.input}
                          required
                          maxLength={6}
                        />
                      </div>
                      <Button
                        type="submit"
                        variant="primary"
                        className="w-full h-12 bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(255,255,255,0.72)] hover:bg-[rgba(45,41,35,0.85)] dark:hover:bg-[rgba(255,255,255,0.85)] text-white dark:text-[#2d2923] hover:text-white dark:hover:text-[#2d2923] font-plus-jakarta font-medium text-[14px] leading-[20px] rounded-lg transition-all duration-200"
                        isLoading={loading}
                      >
                        SUBMIT
                      </Button>
                      <button
                        type="button"
                        onClick={onSendOtp}
                        className="w-full text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors font-plus-jakarta font-medium text-[14px] underline"
                        disabled={sendingOtp}
                      >
                        {sendingOtp ? 'Sending...' : 'Resend OTP code'}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className={typography.labelBlock}>
                      Password
                    </label>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      leftElement={<Lock size={20} className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)]" />}
                      rightElement={
                        <button
                          type="button"
                          onClick={() => onShowPasswordChange(!showPassword)}
                          className="text-[rgba(45,41,35,0.72)] dark:text-[rgba(255,255,255,0.72)] hover:text-[#2d2923] dark:hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      }
                      className={formStyles.input}
                      required
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={(checked) => onRememberMeChange(checked as boolean)}
                        className={formStyles.checkbox}
                      />
                      <span className={typography.secondaryText}>Remember me</span>
                    </label>
                    <Link
                      to="/forgot-password"
                      className={typography.link}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full h-12 bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(255,255,255,0.72)] hover:bg-[rgba(45,41,35,0.85)] dark:hover:bg-[rgba(255,255,255,0.85)] text-white dark:text-[#2d2923] hover:text-white dark:hover:text-[#2d2923] font-plus-jakarta font-medium text-[14px] leading-[20px] rounded-lg transition-all duration-200"
                    isLoading={loading}
                  >
                    SIGN IN
                  </Button>
                </>
              )}
            </form>

            <div className="mt-6 text-center">
              <p className={typography.secondaryText}>
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className={typography.link}
                >
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className={typography.secondaryText}>
            By signing in, you agree to our{' '}
            <Link to="/terms" className={typography.link}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className={typography.link}>
              Privacy Policy
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  )
}

